import React, { useContext } from "react";
import { Prompt, RouteComponentProps } from "react-router-dom";

import ActionBar from "../../components/ActionBar/ActionBar";
import Crumb from "../../components/Crumb/Crumb";
import { Tree } from "../../components/Tree/Tree";
import { LEAVE_PAGE_MESSAGE } from "../../constants/general";
import { SiteContext } from "../../context/Site";
import { UserContext } from "../../context/User";
import { useSearch } from "../../hooks/useApi";
import useApiUrl from "../../hooks/useApiUrl";
import usePublisher from "../../hooks/usePublisher";
import useSite from "../../hooks/useSite";
import Block from "../../style-guide/Block/Block";
import Card from "../../style-guide/Card/Card";
import EmptyState from "../../style-guide/EmptyState/EmptyState";
import Fader from "../../style-guide/Fader/Fader";
import Field from "../../style-guide/Inputs/Field";
import Form from "../../style-guide/Inputs/Form";
import Label from "../../style-guide/Inputs/Label";
import Section from "../../style-guide/Section/Section";
import Text from "../../style-guide/Text/Text";
import { CategoryTreeEntry, Site } from "../../types/apiResponses";

interface Props extends RouteComponentProps<{}> {}

const Exclusions: React.FC<Props> = () => {
  const user = useContext(UserContext).data;
  const siteContext = useContext(SiteContext);
  const site = useSite();
  const isDepartment = !!site.departmentOf;

  const publisher = usePublisher<Site>({
    id: user.site!,
    baseUrl: ["site"],
    baseDashboardUrl: "/innhold/ekskluderinger",
    initialData: {},
    skipId: true,
    onSubmit: (updated: Site) => {
      if (updated) {
        siteContext.setData(updated);
      }
    }
  });

  const form = publisher.form;
  const excludeCategories: string[] =
    form.data.departmentProductFilter?.excludeCategories ?? [];

  const categoryTreeUrl = useApiUrl(["pages", "tree"]);

  const categoryTreeSearch = useSearch<CategoryTreeEntry>({
    endpoint: categoryTreeUrl,
    fetchOnMount: true,
    limit: 299,
    queryParams: {
      onlyInherited: true,
      draft: false
    }
  });

  if (!isDepartment && !form.loadingPrefill) {
    return (
      <Fader>
        <Block hugTop hugBottom>
          <Text element="h1" variant="display3" gutterBottom>
            <Crumb url="/innhold">Innhold</Crumb>
            Ekskluderinger
          </Text>
        </Block>
        <Section>
          <EmptyState
            title="Kun for avdelinger"
            text="Kun avdelinger kan ekskludere innhold."
          />
        </Section>
      </Fader>
    );
  }

  return (
    <Form onSubmit={form.submit}>
      <Prompt when={form.hasMadeChanges} message={() => LEAVE_PAGE_MESSAGE} />
      <Fader>
        <Block hugTop hugBottom>
          <Text element="h1" variant="display3" gutterBottom>
            <Crumb url="/innhold">Innhold</Crumb>
            Ekskluderinger
          </Text>
        </Block>
        <Section>
          <Card>
            <Block>
              <Section hugTop>
                <Text variant="body1" gutterBottom>
                  Valgte sider og kategorier og alle deres undersider og
                  underkategorier vil skjules på nettsiden. Dette inkluderer
                  alle produkter tilhørende valgte kategorier.
                </Text>
                <Text gutterBottom variant="body1">
                  <strong>MERK:</strong>
                  <ul>
                    <li>
                      Hvis et produkt tilhører flere kategorier og en av
                      kategoriene ikke er ekskludert, vil fortsatt produktet
                      vises i nettbutikken.
                    </li>
                    <li>
                      Selv om alle undersider og alle underkategorier av en
                      eksludert side eller kategori automatisk vil bli skjult,
                      vil kun produkter som spesifikt er kategorisert med en
                      eksludert kategori skjules.
                    </li>
                    <li>Maks 100 sider og kategorier.</li>
                  </ul>
                </Text>
              </Section>
              <Field hugBottom>
                <Text element="h3" variant="subtitle" gutterBottom>
                  Ekskluderte sider og kategorier
                </Text>
                <Tree
                  tree={categoryTreeSearch.results}
                  labelField="title"
                  dontCompensate
                  values={excludeCategories}
                  childrenField="children"
                  enableSeach
                  onChange={(id, selected) => {
                    const next = selected
                      ? [...excludeCategories, id]
                      : excludeCategories.filter((c) => c !== id);

                    form.setData({
                      data: {
                        ...form.data,
                        departmentProductFilter: {
                          ...(form.data.departmentProductFilter || {}),
                          excludeCategories: next
                        }
                      }
                    });
                  }}
                />
              </Field>
            </Block>
          </Card>
        </Section>
      </Fader>
      <ActionBar publisher={publisher} />
    </Form>
  );
};

export default Exclusions;
